/* Copyright (c) 2020-present CloudIO Inc */

import {
  action,
  atom,
  emptyFunction,
  useStateXAtom,
  useStateXRef,
} from '@cloudio-saas/statex';
import {
  Button,
  Dialog,
  DialogContentText,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  Theme,
  TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Icon from 'icons/Icon';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo, useEffect, useLayoutEffect, useState } from 'react';

const useStyles = makeStyles((theme: Theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
}));

export interface UserInputDialogProps {
  title: string;
  content: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  defaultName?: string;
  onOk: (
    value: string,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
  onClose?: (
    event: React.SyntheticEvent,
    reason: 'escapeKeyDown' | 'backdropClick' | 'cancelClick',
  ) => void;
  onChange?: (value: string) => string;
  isValid?: (value: string) => boolean;
  feedback?: (value: string) => string;
}

const userInputDialogRefPath = ['transient', 'userInputDialog', 'ref'];

const userInputDialogOpenAtom = atom({
  path: ['transient', 'userInputDialog', 'open'],
  defaultValue: false,
});

export const userInputDialogAction = action<UserInputDialogProps>(
  ({ getRef, set }, props) => {
    const ref = getRef(userInputDialogRefPath);
    if (ref) {
      ref.current = props;
      set(userInputDialogOpenAtom, true);
    }
  },
);

function UserInputDialog() {
  const [open, setOpen] = useStateXAtom(userInputDialogOpenAtom);
  const platform = usePlatform();
  const ref = useStateXRef<UserInputDialogProps>(userInputDialogRefPath, {
    title: '',
    content: '',
    label: '',
    onOk: emptyFunction,
  });
  const classes = useStyles();
  const {
    title,
    content,
    onClose,
    onOk,
    label,
    placeholder,
    required = true,
    defaultName = '',
    onChange = (value) => value,
    isValid = () => true,
    feedback = () => '',
  } = ref.current;
  const [value, setValue] = useState(isValid(defaultName) ? defaultName : '');
  const [feedbackText, setFeedback] = useState(
    defaultName ? feedback(defaultName) : '',
  );

  useEffect(() => {
    setFeedback(feedback(defaultName));
  }, [defaultName, feedback]);

  useLayoutEffect(() => {
    const { isValid: _isValid = () => true } = ref.current;
    setValue(_isValid(defaultName) ? defaultName : '');
  }, [defaultName, open, ref]);

  if (!open) {
    return null;
  }

  const handleClose = (
    event: React.SyntheticEvent,
    reason: 'escapeKeyDown' | 'backdropClick' | 'cancelClick',
  ) => {
    setOpen(false);
    setValue('');
    onClose?.(event, reason);
  };

  const handleOk = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (required && value.trim().length === 0) {
      return;
    }
    platform.forceUpdater(() => {
      setOpen(false);
    });
    setValue('');
    onOk(value.trim(), event);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      id="io_dialog"
      sx={[{ bgcolor: 'transparent !important' }]}
    >
      <DialogTitle sx={{ marginRight: '60px' }}>
        {title}
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={(e) => handleClose(e, 'cancelClick')}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ minWidth: '600px' }}>
        <DialogContentText sx={{ marginBottom: 4 }}>
          {content}
        </DialogContentText>
        <TextField
          placeholder={placeholder}
          variant="standard"
          autoFocus
          fullWidth
          required={required}
          label={label}
          value={value}
          onChange={(e) => {
            const val = onChange(e.target.value);
            if (isValid(val)) {
              setValue(val);
              setFeedback(feedback(val));
            }
          }}
          helperText={feedbackText}
        />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={(e) => handleClose(e, 'cancelClick')}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleOk}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(UserInputDialog);
